/* ============================================================
   Senchado 2.0 — 道具一覧の並び順（重み付き日替わりシャッフル）純関数
   仕様SSOT: docs/spec-dougu-discovery.md §並び順
   契約テスト: tests/senchado-shuffle.test.mjs
   ------------------------------------------------------------
   なぜ作り直したか（2026-08-12 実測）
     旧: コレクションの手動順（＝発売日が新しい順）をそのまま表示していた。
     現: 商品レコードの作成日の降順。これが問題で、
         - 422件中 398件（94.3%）が初期24件に**一生**入らない
         - 作成日は死んだ軸（直近14日に作られた道具レコードは0件）＝棚が何ヶ月も固まる
         - 初期24件が持つ12ヶ月売上は 旧40.6% → 現行4.3%
     この面には性質の違う2つの目的が同居している:
         フロー1 発売の瞬間（早い者勝ち）  … 実測では一覧では解けない
                 1時間以内完売24件を買った58注文のうち一覧着地は3件(5.2%)、
                 商品ページ直着地56.9%、発売24h以内注文の50.0%が通知経由。
         フロー2 常時の売り場（滞留を動かす・見ていない商品に出会う）… ここを解く
   ------------------------------------------------------------
   構成（初期24枠の内訳。売切は現行どおり常に下部）
     ピン   最大6枠  決め打ち。日替わりしない。売上を戻しているのはここ
     ローテ 最大14枠 下限保証。28日周期で全商品に必ず順番が回る
     抽選   残り     ここだけ重みが効く。3日ごとに引き直す
   ------------------------------------------------------------
   公開API（window/document を参照しないので Node から import 可能）
     hash32(str)                     → FNV-1a 32bit
     mulberry32(seed)                → () => [0,1) のシード付き乱数
     uOf(key, id)                    → 決定的な [0,1)
     jstParts(date)                  → {yyyy,mm,dd}（Asia/Tokyo）
     epochDayJST(date, cutoffHour)   → 日番号（cutoffHour 始まり）
     lotKeyOf(date, opts)            → 抽選キー（既定3日固定）
     weightOf(signals, date)         → 抽選の重み 1.0〜12.0
     rotationDay(id, cycle)          → その商品のローテ担当日（0..cycle-1）
     orderProducts(items, opts)      → 並べ替えた配列（非破壊）
   ============================================================ */

(function () {
  'use strict';

  if (typeof window !== 'undefined' && window.SenchadoShuffle) { return; }

  // 既定値。呼び出し側から opts で上書きできる（テストのため）
  var DEFAULTS = {
    cutoffHour: 4,   // 並びが切り替わる時刻（JST）。谷本決裁 2026-08-12。
                     // 0時だと「夜中に見ている人が切り替わりを踏む」ので4時。
    lotDays: 3,      // 抽選の据え置き日数。谷本決裁。1日だと翌日に24枠中7.4枚しか
                     // 残らないが、3日だと12.7枚（53%）残る。「日が変わると変わる」は
                     // 満たしつつ、棚の半分は昨日の面影が残る。
    dropSlots: 8,    // 「今日発売・今週入荷」の枠。谷本決裁 2026-08-12。
                     // 実測: 過去12ヶ月のドロップ42回は中央値3件・最大7件（大型再販の
                     // 2回を除く）。過去90日で7日窓に8件を超えた日は0日なので溢れない。
    dropDays: 7,     // 発売からこの日数だけ枠に載せる。発売は毎週水曜08:00なので、
                     // 7日でちょうど次のドロップと入れ替わる。
    pinSlots: 6,
    rotCycle: 28,
    rotMax: 18,      // 🔴 14 では足りない。223件を28日にハッシュで配ると担当群は
                     // 平均8.0だが min3 / **max18** まで振れる（実測）。14で切ると
                     // 溢れた担当が抽選に落ち、28日カバー率が 100% → 98.7% に欠ける。
                     // 18 にすると 28日で223件すべてが初期24件に載る。
                     // ピン6と合わせて24枠ちょうど＝担当が18の日（28日中2日）は
                     // 抽選枠が出ないが、下限保証のほうを優先する。
    staleFloorDays: 90,   // ここまでは滞留とみなさない
    staleCeilDays: 365,   // ここで滞留スコア満点
    freshDays: 60,        // 発売からこの日数で新着スコアが0になる
    staleWeight: 3,       // A: 滞留の効き。3→6で露出5.41→6.42件/日と逓減するので3
    freshWeight: 2        // B: 新着の効き
  };

  // ---------------------------------------------------------------- 乱数

  // FNV-1a。文字列 → 32bit 符号なし整数
  function hash32(str) {
    var h = 2166136261 >>> 0;
    var s = String(str);
    for (var i = 0; i < s.length; i++) {
      h ^= s.charCodeAt(i);
      h = Math.imul(h, 16777619) >>> 0;
    }
    return h >>> 0;
  }

  function mulberry32(seed) {
    var a = seed >>> 0;
    return function () {
      a = (a + 0x6D2B79F5) >>> 0;
      var t = a;
      t = Math.imul(t ^ (t >>> 15), t | 1);
      t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
      return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
  }

  /**
   * 決定的な [0,1)。
   * 🔴 hash32 の出力をそのまま /2^32 して使わないこと。
   *    実データ422件で120日回すと、上位24件しきい値の該当数が
   *    最小1件〜最大53件・sd 9.98（理論sd 4.63の2.2倍）に散らばる。
   *    mulberry32 を1回通すと min15/max35/sd 4.54 で理論値に一致する。
   *    FNV-1a は近い入力（連番ID）に対する出力の散らばりが弱いため。
   */
  function uOf(key, id) {
    return mulberry32(hash32(String(key) + ':' + String(id)))();
  }

  // ---------------------------------------------------------------- 日付

  // ストアTZ（Asia/Tokyo）での {yyyy,mm,dd}。assets/senchado-sekki.js と同型。
  // 🔴 Liquid の {{ 'now' | date }} は使えない。/collections/dougu も ?section_id= も
  //    etag: page_cache:... を返すため、日付がキャッシュに焼き付いて
  //    1ページ目と2ページ目が別の日の並びになる。日付は必ずクライアントで出す。
  function jstParts(date) {
    var d = date || new Date();
    try {
      var parts = new Intl.DateTimeFormat('en-US', {
        timeZone: 'Asia/Tokyo', year: 'numeric', month: '2-digit', day: '2-digit'
      }).formatToParts(d);
      var yyyy = 0, mm = 0, dd = 0;
      for (var i = 0; i < parts.length; i++) {
        if (parts[i].type === 'year') yyyy = parseInt(parts[i].value, 10);
        if (parts[i].type === 'month') mm = parseInt(parts[i].value, 10);
        if (parts[i].type === 'day') dd = parseInt(parts[i].value, 10);
      }
      if (yyyy >= 1 && mm >= 1 && dd >= 1) return { yyyy: yyyy, mm: mm, dd: dd };
    } catch (e) { /* Intl 不可環境 → ローカル日付にフォールバック */ }
    return { yyyy: d.getFullYear(), mm: d.getMonth() + 1, dd: d.getDate() };
  }

  /**
   * 「並びの日」を表す通し番号。cutoffHour（既定4時JST）を境に繰り上がる。
   * 端末のローカル時計やTZに依存させない（海外から見ている人が別の棚を見ないように）。
   */
  function epochDayJST(date, cutoffHour) {
    var h = (cutoffHour === undefined || cutoffHour === null) ? DEFAULTS.cutoffHour : cutoffHour;
    var base = date ? new Date(date.getTime()) : new Date();
    var shifted = new Date(base.getTime() - h * 3600000);
    var p = jstParts(shifted);
    return Math.floor(Date.UTC(p.yyyy, p.mm - 1, p.dd) / 86400000);
  }

  // 抽選キー。lotDays 日ごとに1回だけ変わる。
  function lotKeyOf(date, opts) {
    var o = withDefaults(opts);
    var day = epochDayJST(date, o.cutoffHour);
    return 'L' + Math.floor(day / Math.max(1, o.lotDays));
  }

  function withDefaults(opts) {
    var o = {};
    for (var k in DEFAULTS) { if (Object.prototype.hasOwnProperty.call(DEFAULTS, k)) o[k] = DEFAULTS[k]; }
    if (opts) { for (var j in opts) { if (Object.prototype.hasOwnProperty.call(opts, j) && opts[j] !== undefined && opts[j] !== null) o[j] = opts[j]; } }
    return o;
  }

  function clamp01(x) { return x < 0 ? 0 : (x > 1 ? 1 : x); }

  // 'YYYY-MM-DD' → その日からの経過日数（JST基準・不正値は null）
  function daysSinceIso(iso, date, cutoffHour) {
    if (!iso) return null;
    var m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(iso));
    if (!m) return null;
    var then = Math.floor(Date.UTC(+m[1], +m[2] - 1, +m[3]) / 86400000);
    return epochDayJST(date, cutoffHour) - then;
  }

  // ---------------------------------------------------------------- 重み

  /**
   * 抽選の重み。値域 1.0〜12.0。
   *   S 滞留  90日で0 → 365日以上で1（線形）
   *   F 新着  発売当日1 → 60日以降0（線形）
   *   w = (1 + A·S) × (1 + B·F)
   *
   * 乗算にする理由: 加算合成だと歪んだ信号が薄まり、初期24件の売上構成比が7.0%まで落ちる。
   * 対数・パーセンタイル変換は使わない: S も F も 0 に床がある連続量なので、
   *   変換で全商品に下駄を配ると比が潰れる。
   *
   * 🔴 売れ筋（wnet365）はここに入れない。
   *   実測: 入れると web +1.2pt の代わりに滞留露出が 5.41→3.45件/日（−36%）。
   *   売れ筋はピン枠が既に拾っているので、抽選でも効かせると二重になる。
   *   売上を積みたいならピン枠を6→8に増やす方が効率がよい。
   *
   * 🔴 在庫数も入れない。滞留42件の在庫中央値11点 vs 他181件9点で、
   *   在庫の深さは滞留と直交している（倍率1.04倍）。加えて BOPIS を有効にすると
   *   inventory_quantity に店頭受取ミラーが混ざる（docs/spec-bopis-inventory-display.md）。
   *
   * @param {{lastSold?: string|null, sellStart?: string|null}} signals
   */
  function weightOf(signals, date, opts) {
    var o = withDefaults(opts);
    var s = signals || {};

    // last_sold が null＝直近365日で1点も売れていない＝滞留スコア満点
    var soldDays = daysSinceIso(s.lastSold, date, o.cutoffHour);
    var S = (soldDays === null)
      ? 1
      : clamp01((soldDays - o.staleFloorDays) / (o.staleCeilDays - o.staleFloorDays));

    // 新着スコア。3通りに分かれる:
    //   発売日なし … 定番中心（在庫あり223件中112件が該当）。新着ではないので 0
    //   未来日     … 予約販売中。もうすぐ出るので満点
    //   過去日     … 発売当日1 → freshDays 日で 0 まで線形に落ちる
    var freshDays = daysSinceIso(s.sellStart, date, o.cutoffHour);
    var F;
    if (freshDays === null) F = 0;
    else if (freshDays < 0) F = 1;
    else F = clamp01((o.freshDays - freshDays) / o.freshDays);

    return (1 + o.staleWeight * S) * (1 + o.freshWeight * F);
  }

  /**
   * その商品がローテ枠を担当する日（0..cycle-1）。商品ごとに固定で一生変わらない。
   *
   * プールの件数を数えなくてよいのが要点。「今日の担当は上位N件」方式だと
   * 他の商品が売り切れた瞬間に自分の順番がずれるが、この方式はずれない。
   * 実測（最悪無露出間隔）: churn 0%で28日 / 2%で56日 / 5%で78日。
   * プール順列方式は同条件で 65→116→97日と劣化する。
   */
  function rotationDay(id, cycle) {
    var c = Math.max(1, cycle || DEFAULTS.rotCycle);
    return Math.floor(uOf('ROT', id) * c) % c;
  }

  // ---------------------------------------------------------------- 並べ替え

  /**
   * 商品を並べ替える（非破壊）。
   *
   * @param {Array} items 各要素は
   *   { id, available, pin?, wnet365?, lastSold?, sellStart? }
   *   id は product.id（数値ID）を使う。**handle は使わない**——
   *   道具441件中5件のハンドルが日本語を含み、リネームすると並びの履歴が飛ぶ。
   * @param {Object} opts { now, handle, ...DEFAULTS }
   * @returns {Array} 並べ替え後の配列
   */
  function orderProducts(items, opts) {
    var o = withDefaults(opts);
    var now = (opts && opts.now) || new Date();
    var handle = (opts && opts.handle) || '';
    var list = (items || []).slice();

    // 売切は常に下部。混ぜると見出しの売上%はほぼ同じでも、
    // 24枠中9.8枚が「買えないカード」になる（実測）。
    var avail = [], sold = [];
    for (var i = 0; i < list.length; i++) {
      (list[i] && list[i].available ? avail : sold).push(list[i]);
    }

    var taken = {};
    var out = [];

    // --- 発売枠（今日発売・今週入荷） ---------------------------
    // 谷本決裁: 「今日入ったよ、今日発売開始だよ、というものは上位に出す」。
    // ただし実測では、発売直後の争奪戦そのものはこの面では取れない
    // （1時間以内完売24件の購入者58注文のうち一覧着地は3件＝5.2%、
    //   発売24h以内の注文の50.0%は newsletter/LINE 経由）。
    // だからこの枠の役割は「勝ちに行く導線」ではなく「今週なにか入ったと分かる場」。
    // それには 1〜8件で足りる。
    //
    // ⚠️ 載る条件は sell_start_date を持っていること。作家もの211件のうち
    //    発売日が入っているのは108件（51%）しかないので、入力されていない器は
    //    この枠に出ない。枠を広げても解決しない（データ側の問題）。
    var drops = [];
    for (var d0 = 0; d0 < avail.length; d0++) {
      var ds = daysSinceIso(avail[d0].sellStart, now, o.cutoffHour);
      if (ds === null) continue;
      if (ds >= 0 && ds < o.dropDays) drops.push(avail[d0]);
    }
    // 新しい発売が上。同日はその日のうちで決定的に混ぜる（毎回同じ作家が先頭にならないように）
    var dropLot = lotKeyOf(now, o);
    drops.sort(function (a, b) {
      var da = daysSinceIso(a.sellStart, now, o.cutoffHour);
      var db = daysSinceIso(b.sellStart, now, o.cutoffHour);
      if (da !== db) return da - db;
      return (uOf(dropLot + ':DROP', a.id) - uOf(dropLot + ':DROP', b.id)) || cmpId(a, b);
    });
    drops = drops.slice(0, o.dropSlots);
    for (var d1 = 0; d1 < drops.length; d1++) { taken[drops[d1].id] = 1; out.push(drops[d1]); }

    // --- ピン枠 -------------------------------------------------
    // 🔴 毎日「在庫ありの中から」選び直す。固定IDで持つと売切れた瞬間に枠が死に、
    //    初期24件の売上構成比が純ランダムより悪くなる（web 5.0% vs 5.8%）。
    var pinned = [];
    for (var p = 0; p < avail.length; p++) {
      if (taken[avail[p].id]) continue;   // 発売枠に出ている器を二重に置かない
      var pv = toInt(avail[p].pin);
      if (pv >= 1 && pv <= o.pinSlots) pinned.push(avail[p]);
    }
    pinned.sort(function (a, b) {
      return (toInt(a.pin) - toInt(b.pin)) || cmpId(a, b);
    });
    pinned = pinned.slice(0, o.pinSlots);

    // 空き枠は web純売上の降順で自動充当。山上さんが1週間触らなくても棚が崩れない。
    if (pinned.length < o.pinSlots) {
      for (var q = 0; q < pinned.length; q++) taken[pinned[q].id] = 1;
      var fill = [];
      for (var r = 0; r < avail.length; r++) {
        if (!taken[avail[r].id]) fill.push(avail[r]);
      }
      fill.sort(function (a, b) {
        return (num(b.wnet365) - num(a.wnet365)) || cmpId(a, b);
      });
      pinned = pinned.concat(fill.slice(0, o.pinSlots - pinned.length));
    }
    for (var t = 0; t < pinned.length; t++) { taken[pinned[t].id] = 1; out.push(pinned[t]); }

    // --- ローテ枠（下限保証） -----------------------------------
    var today = epochDayJST(now, o.cutoffHour);
    var slot = ((today % o.rotCycle) + o.rotCycle) % o.rotCycle;
    var rotated = [];
    for (var u = 0; u < avail.length; u++) {
      if (taken[avail[u].id]) continue;
      if (rotationDay(avail[u].id, o.rotCycle) === slot) rotated.push(avail[u]);
    }
    // 担当群の中の順番も決定的に（日によって入れ替わってよい）
    var rotLot = lotKeyOf(now, o);
    rotated.sort(function (a, b) {
      return (uOf(rotLot + ':ROTSORT', a.id) - uOf(rotLot + ':ROTSORT', b.id)) || cmpId(a, b);
    });
    rotated = rotated.slice(0, o.rotMax);
    for (var v = 0; v < rotated.length; v++) { taken[rotated[v].id] = 1; out.push(rotated[v]); }

    // --- 抽選枠 -------------------------------------------------
    // A-Res（Efraimidis-Spirakis）: key = u^(1/w) の降順で重み付き無作為抽出になる。
    var lot = lotKeyOf(now, o);
    var rest = [];
    for (var w = 0; w < avail.length; w++) {
      if (taken[avail[w].id]) continue;
      var it = avail[w];
      var wt = weightOf({ lastSold: it.lastSold, sellStart: it.sellStart }, now, o);
      // u=0 だと 0^(1/w)=0 で常に最下位に固定されてしまうので下限を置く
      var uu = uOf(lot, it.id);
      if (uu < 1e-9) uu = 1e-9;
      rest.push({ item: it, key: Math.pow(uu, 1 / wt) });
    }
    rest.sort(function (a, b) { return (b.key - a.key) || cmpId(a.item, b.item); });
    for (var x = 0; x < rest.length; x++) out.push(rest[x].item);

    // --- 売切（下部）。ここも日替わりで動かす（同じ売切が毎日先頭だと単調） ---
    sold.sort(function (a, b) {
      return (uOf(lot + ':SOLD', a.id) - uOf(lot + ':SOLD', b.id)) || cmpId(a, b);
    });
    for (var y = 0; y < sold.length; y++) out.push(sold[y]);

    return out;
  }

  function toInt(v) { var n = parseInt(v, 10); return isNaN(n) ? 0 : n; }
  function num(v) { var n = Number(v); return isFinite(n) ? n : 0; }
  // 同点は id で決める。ここを乱数にすると同日内で並びがブレる
  function cmpId(a, b) { return String(a && a.id).localeCompare(String(b && b.id)); }

  var API = {
    hash32: hash32,
    mulberry32: mulberry32,
    uOf: uOf,
    jstParts: jstParts,
    epochDayJST: epochDayJST,
    lotKeyOf: lotKeyOf,
    weightOf: weightOf,
    rotationDay: rotationDay,
    orderProducts: orderProducts,
    DEFAULTS: DEFAULTS
  };

  if (typeof module !== 'undefined' && module.exports) {
    module.exports = API;
  }
  if (typeof window !== 'undefined') {
    window.SenchadoShuffle = API;
  }
})();
